import { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ComplaintsModal from './ComplaintsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const columns = [
  { heading: 'Product', links: [
    { label: 'Vector Settle', to: '#property' },
    { label: 'Vector Bridge', to: '#property' },
    { label: 'Bulk Pay', to: '#business' },
    { label: 'Vector Forward', to: '#business' },
    { label: 'Global Payroll', to: '#business' },
    { label: 'Multi-currency account', to: '#personal' },
  ]},
  { heading: 'Company', links: [
    { label: 'About us', to: '#about' },
    { label: 'Careers', to: '#about' },
    { label: 'Press', to: '#' },
    { label: 'Newsroom', to: '#' },
    { label: 'Contact', to: '#' },
  ]},
  { heading: 'Resources', links: [
    { label: 'FX insights', to: '#' },
    { label: 'Case studies', to: '#' },
    { label: 'Help centre', to: '#' },
    { label: 'Status', to: '#' },
  ]},
  { heading: 'Legal', links: [
    { label: 'Terms of use', to: '/terms' },
    { label: 'Privacy notice', to: '/privacy' },
    { label: 'Cookie policy', to: '/cookies' },
    { label: 'Complaints', to: 'complaints' },
    { label: 'Modern slavery', to: '/modern-slavery' },
  ]},
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [complaintsOpen, setComplaintsOpen] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email });
      if (res.data?.status === 'already_subscribed') {
        toast.success('You are already on the list.');
      } else {
        toast.success('Subscribed. Look out for our next briefing.');
      }
      setEmail('');
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg =
        typeof d === 'string'
          ? d
          : Array.isArray(d)
          ? d.map((e) => e?.msg || 'Invalid value').join(', ')
          : 'Could not subscribe. Please try again.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const renderLink = (l) => {
    if (l.to === 'complaints') {
      return (
        <button
          type="button"
          onClick={() => setComplaintsOpen(true)}
          data-testid="footer-complaints-link"
          className="text-[14px] text-[#F5F1E8]/75 hover:text-[#F5F1E8] transition-colors"
        >
          {l.label}
        </button>
      );
    }
    if (l.to.startsWith('/')) {
      return (
        <Link to={l.to} className="text-[14px] text-[#F5F1E8]/75 hover:text-[#F5F1E8] transition-colors">
          {l.label}
        </Link>
      );
    }
    return (
      <a href={l.to} className="text-[14px] text-[#F5F1E8]/75 hover:text-[#F5F1E8] transition-colors">
        {l.label}
      </a>
    );
  };

  return (
    <footer className="relative bg-[#0B3B2E] text-[#F5F1E8] pt-20 pb-8 overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#C8944B]/8 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 pb-16 border-b border-[#F5F1E8]/15">
          <div className="lg:col-span-6">
            <h3 className="font-serif text-[36px] lg:text-[52px] leading-[1.02]">
              Move money<br />
              <span className="italic text-[#C8944B]">with direction.</span>
            </h3>
            <p className="mt-4 text-[15px] text-[#F5F1E8]/70 max-w-[440px]">
              Monthly FX briefings and product updates. No spam, unsubscribe in one click.
            </p>
          </div>
          <div className="lg:col-span-6 lg:pt-4">
            <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-[520px] lg:ml-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="flex-1 h-12 px-5 rounded-full bg-[#F5F1E8]/8 border border-[#F5F1E8]/20 text-[#F5F1E8] placeholder-[#F5F1E8]/45 outline-none focus:border-[#C8944B] transition-colors"
              />
              <button
                type="submit"
                disabled={busy}
                className="h-12 px-6 rounded-full bg-[#C8944B] text-[#0B3B2E] font-medium hover:bg-[#D9A85C] transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-70"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending</>
                ) : (
                  <>Subscribe <ArrowUpRight className="w-4 h-4" /></>
                )}
              </button>
            </form>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 py-14">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-[#C8944B] flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M4 20 L12 4 L20 20" stroke="#0B3B2E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="font-serif text-[22px] font-semibold">
                Vector<span className="text-[#C8944B]">FX</span>
              </span>
            </div>
            <p className="mt-4 text-[13px] text-[#F5F1E8]/60 leading-relaxed">
              12 New Fetter Lane<br />
              London EC4A 1JP<br />
              United Kingdom
            </p>
          </div>

          {columns.map((c) => (
            <div key={c.heading}>
              <h4 className="font-serif text-[15px] text-[#C8944B] tracking-wide">{c.heading}</h4>
              <ul className="mt-4 space-y-2.5">
                {c.links.map((l) => (
                  <li key={l.label}>{renderLink(l)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-[#F5F1E8]/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-[12px] text-[#F5F1E8]/55 max-w-[720px] leading-relaxed">
            © 2025 Vector FX Ltd. Authorised by the Financial Conduct Authority under the Payment
            Services Regulations 2017 (Firm Reference No. 900000). Registered in England and Wales,
            company no. 12345678.
          </p>
          <div className="flex items-center gap-4 text-[12px] text-[#F5F1E8]/55">
            <a href="#" className="hover:text-[#F5F1E8]">EN · GBP</a>
            <span className="w-1 h-1 rounded-full bg-[#F5F1E8]/25" />
            <a href="#" className="hover:text-[#F5F1E8]">LinkedIn</a>
            <a href="#" className="hover:text-[#F5F1E8]">X</a>
          </div>
        </div>
      </div>
      <ComplaintsModal open={complaintsOpen} onOpenChange={setComplaintsOpen} />
    </footer>
  );
}
